import { createStyles, alpha, makeStyles, Theme } from '@material-ui/core/styles';

export const useRolesStyles = makeStyles((theme: Theme) => {
  const dark = theme.palette.type === 'dark';
  return createStyles({
    paper: {
      borderRadius: 16,
      overflow: 'hidden',
    },
    companyRow: {
      '& + $companyRow': {
        borderTop: `1px solid ${theme.palette.divider}`,
      },
    },
    companyHeader: {
      display: 'flex',
      alignItems: 'center',
      padding: '16px 20px',
      cursor: 'pointer',
    },
    companyHeaderStatic: {
      cursor: 'default',
    },
    companyAvatar: {
      width: 40,
      height: 40,
      borderRadius: 10,
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 14,
      fontWeight: 700,
      color: theme.palette.primary.main,
      backgroundColor: alpha(theme.palette.primary.main, dark ? 0.16 : 0.1),
    },
    companyInfo: {
      flex: 1,
      marginLeft: 14,
      minWidth: 0,
    },
    companyName: {
      display: 'flex',
      alignItems: 'center',
      fontSize: 15,
      fontWeight: 600,
      color: theme.palette.text.primary,
    },
    ownerCrown: {
      marginLeft: 8,
      color: theme.palette.warning.main,
      display: 'inline-flex',
    },
    companySubtitle: {
      marginTop: 2,
      fontSize: 12.5,
      color: theme.palette.text.secondary,
    },
    chevron: {
      color: theme.palette.text.secondary,
      display: 'inline-flex',
      transition: 'transform .15s',
    },
    chevronOpen: {
      transform: 'rotate(180deg)',
    },
    moduleList: {
      padding: '4px 20px 20px',
    },
    moduleRow: {
      backgroundColor: theme.palette.background.default,
      borderRadius: 12,
      padding: '10px 14px',
      '& + $moduleRow': {
        marginTop: 6,
      },
    },
    moduleRowMain: {
      display: 'flex',
      alignItems: 'center',
      minHeight: 32,
    },
    moduleIcon: {
      display: 'inline-flex',
      color: theme.palette.text.secondary,
      flexShrink: 0,
    },
    moduleLabel: {
      flex: 1,
      marginLeft: 12,
      marginRight: 12,
      fontSize: 14,
      fontWeight: 500,
      color: theme.palette.text.primary,
    },
    moduleDetail: {
      marginTop: 10,
      padding: '12px 14px',
      borderRadius: 10,
      border: `1px solid ${alpha(theme.palette.primary.main, dark ? 0.24 : 0.12)}`,
      backgroundColor: theme.palette.background.paper,
    },
    detailToggle: {
      marginLeft: 8,
      padding: 6,
    },
    saveBar: {
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
      marginTop: 14,
      flexWrap: 'wrap',
      gap: 8,
    },
    saveBarError: {
      flex: 1,
      minWidth: 220,
      fontSize: 12.5,
      lineHeight: 1.4,
      color: theme.palette.error.main,
    },
    // One bar for the whole page. Sticky rather than fixed: the shell is
    // `root{display:flex}` → Sidebar + `main{flexGrow:1}` with the scrollport on
    // <html>, so a sticky child of the page content already spans the content
    // column and can't run under the sidebar — no drawer-width maths, correct on
    // mobile for free. It also keeps its place in the flow, so nothing below it is
    // permanently covered and the host needs no bottom spacer.
    pageSaveBar: {
      position: 'sticky',
      bottom: 0,
      // Only has to beat page content: MUI's modal (1300) and snackbar (1400) must
      // still come out on top.
      zIndex: 2,
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: 10,
      marginTop: 16,
      padding: '12px 16px',
      // pay-app floats a chat button bottom-right on every page — leave it room.
      paddingRight: 88,
      borderRadius: 16,
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
      boxShadow: theme.shadows[8],
      [theme.breakpoints.down('xs')]: {
        paddingRight: 16,
      },
    },
    pageSaveBarText: {
      flex: 1,
      minWidth: 200,
      fontSize: 13,
      fontWeight: 500,
      color: theme.palette.text.primary,
    },
    pageSaveBarError: {
      marginTop: 2,
      fontSize: 12,
      fontWeight: 400,
      lineHeight: 1.4,
      color: theme.palette.error.main,
    },
    pageSaveBarActions: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      [theme.breakpoints.down('xs')]: {
        width: '100%',
        '& > *': {
          flex: 1,
        },
      },
    },
    unsavedChip: {
      marginLeft: 8,
      padding: '1px 8px',
      borderRadius: 999,
      fontSize: 10.5,
      fontWeight: 600,
      whiteSpace: 'nowrap',
      color: theme.palette.warning.dark,
      backgroundColor: alpha(theme.palette.warning.main, dark ? 0.24 : 0.16),
    },
    readonlyCaption: {
      padding: '0 20px 16px',
      fontSize: 12.5,
      color: theme.palette.text.disabled,
    },
    emptyRoot: {
      padding: '80px 24px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
    },
    emptyIconWrap: {
      width: 64,
      height: 64,
      borderRadius: 16,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: theme.palette.primary.main,
      backgroundColor: alpha(theme.palette.primary.main, dark ? 0.16 : 0.1),
      marginBottom: 16,
    },
    emptyTitle: {
      fontSize: 17,
      fontWeight: 600,
      color: theme.palette.text.primary,
    },
    emptySubtitle: {
      marginTop: 8,
      fontSize: 13.5,
      maxWidth: 380,
      lineHeight: 1.5,
      color: theme.palette.text.secondary,
    },
    moduleSubtitle: {
      marginTop: 2,
      fontSize: 11.5,
      color: theme.palette.text.secondary,
    },
    // Stands in for the role chip on modules that have no assignable role.
    permissionCount: {
      fontSize: 12.5,
      whiteSpace: 'nowrap',
      color: theme.palette.text.secondary,
    },
    sectionLabel: {
      fontSize: 11.5,
      fontWeight: 600,
      letterSpacing: '.5px',
      textTransform: 'uppercase',
      color: theme.palette.primary.main,
      marginBottom: 10,
    },
    scopeBlock: {
      padding: '12px 14px',
      borderRadius: 10,
      border: `1px solid ${theme.palette.divider}`,
      backgroundColor: theme.palette.background.default,
      '& + $scopeBlock': {
        marginTop: 8,
      },
    },
    scopeHeader: {
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
    },
    scopeTitle: {
      fontSize: 13,
      fontWeight: 600,
      color: theme.palette.text.primary,
      marginRight: 8,
    },
    scopeSummary: {
      marginLeft: 8,
      fontSize: 12,
      color: theme.palette.text.secondary,
    },
    // AppToggle stretches both segments to an equal half of its container, so a
    // fixed width clipped the longer label ('All pay schedules'). Let the group
    // size to its content instead.
    scopeToggle: {
      marginLeft: 'auto',
      flexShrink: 0,
      height: 32,
      '& .MuiToggleButtonGroup-root': {
        width: 'auto',
      },
      '& .MuiToggleButton-root': {
        width: 'auto',
        padding: '0 14px',
        whiteSpace: 'nowrap',
      },
    },
    scopeError: {
      marginTop: 8,
      fontSize: 12,
      color: theme.palette.error.main,
    },
    scopeCaption: {
      marginTop: 8,
      fontSize: 12,
      lineHeight: 1.5,
      color: theme.palette.text.secondary,
    },
    permissionSection: {
      marginTop: 14,
      paddingTop: 14,
      borderTop: `1px dashed ${alpha(theme.palette.primary.main, dark ? 0.3 : 0.18)}`,
    },
    permissionGroup: {
      '& + $permissionGroup': {
        marginTop: 12,
      },
    },
    permissionGroupTitle: {
      fontSize: 12,
      fontWeight: 600,
      color: theme.palette.text.secondary,
      marginBottom: 4,
    },
    permissionRow: {
      display: 'flex',
      alignItems: 'center',
      padding: '3px 0',
    },
    permissionRowConflict: {
      backgroundColor: alpha(theme.palette.error.main, dark ? 0.18 : 0.08),
      borderRadius: 8,
      paddingLeft: 4,
      paddingRight: 4,
    },
    permissionLabel: {
      fontSize: 13,
      color: theme.palette.text.primary,
      marginRight: 8,
    },
    permissionLabelLocked: {
      color: theme.palette.text.secondary,
    },
    badge: {
      marginLeft: 8,
      padding: '1px 8px',
      borderRadius: 999,
      fontSize: 10.5,
      fontWeight: 600,
      whiteSpace: 'nowrap',
    },
    badgeAdded: {
      color: theme.palette.success.main,
      backgroundColor: alpha(theme.palette.success.main, dark ? 0.2 : 0.12),
    },
    badgeRemoved: {
      color: theme.palette.error.main,
      backgroundColor: alpha(theme.palette.error.main, dark ? 0.2 : 0.12),
    },
    badgeLocked: {
      color: theme.palette.text.secondary,
      backgroundColor: alpha(theme.palette.text.secondary, dark ? 0.22 : 0.12),
    },
    banner: {
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: 10,
      marginTop: 14,
      padding: '10px 12px',
      borderRadius: 10,
      fontSize: 12.5,
      lineHeight: 1.4,
    },
    bannerWarning: {
      color: theme.palette.warning.dark,
      backgroundColor: alpha(theme.palette.warning.main, dark ? 0.2 : 0.14),
      border: `1px solid ${alpha(theme.palette.warning.main, dark ? 0.4 : 0.3)}`,
    },
    bannerError: {
      color: theme.palette.error.main,
      backgroundColor: alpha(theme.palette.error.main, dark ? 0.2 : 0.1),
      border: `1px solid ${alpha(theme.palette.error.main, dark ? 0.4 : 0.3)}`,
    },
    bannerInfo: {
      color: theme.palette.text.primary,
      backgroundColor: alpha(theme.palette.primary.main, dark ? 0.18 : 0.08),
      border: `1px solid ${alpha(theme.palette.primary.main, dark ? 0.4 : 0.25)}`,
    },
    bannerText: {
      flex: 1,
      minWidth: 180,
    },
    readOnlyBanner: {
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      margin: '16px 20px 0',
      padding: '10px 14px',
      borderRadius: 12,
      fontSize: 12.5,
      color: theme.palette.warning.dark,
      backgroundColor: alpha(theme.palette.warning.main, dark ? 0.2 : 0.14),
    },
    resourceRow: {
      display: 'flex',
      alignItems: 'center',
      width: '100%',
    },
    resourceRowLabel: {
      flex: 1,
      minWidth: 0,
    },
    resourceDetail: {
      margin: '4px 0 8px 32px',
      padding: '10px 12px',
      borderRadius: 10,
      border: `1px solid ${theme.palette.divider}`,
      backgroundColor: theme.palette.background.default,
    },
    exceptionBadge: {
      marginLeft: 8,
      padding: '1px 8px',
      borderRadius: 999,
      fontSize: 10.5,
      fontWeight: 600,
      whiteSpace: 'nowrap',
      color: theme.palette.warning.dark,
      backgroundColor: alpha(theme.palette.warning.main, dark ? 0.24 : 0.16),
    },
  });
});
